import { useCallback, useEffect, useMemo, useState } from "react";
import { useFolhaPagamentoCadastroPagination } from "../hooks/FolhaPagamento_cadastro/pagination";
import { ItemFolhaPagamento } from "../../domain/models/item_folha_pagamento";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useLocation } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useFolhaPagamentoCadastroFilters } from "../hooks/FolhaPagamento_cadastro/filter";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { FolhaPagamento } from "../../domain/models/folha_pagamento";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ItemFolhaPagamentoForm, ItemFolhaPagamentoSchema } from "../schemas/FolhaPagamento_cadastro/item_folha_pagamento";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { FuncionarioInformation } from "../../../../common/domain/models/funcionario_information";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { SubcontaDropdown } from "../../domain/models/subconta_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";

/**
 * LoadFolhaPagamentoItensState
 */

type LoadFolhaPagamentoItensState = {
  idFolhaPagamento?: number;
  idCentroCusto?: number | null;
  idFuncionario?: number | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useFolhaPagamentoCadastroViewModel
 */

export const useFolhaPagamentoCadastroViewModel = () => {
  const {
    commons: {
      getFuncionariosByIdCentroCustoDropDownUseCase,
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getFuncionarioByIdUsuarioUseCase,
    },
    rh: {
      getAllFolhaPagamentoItensUseCase,
      getFolhaPagamentoUseCase,
      createItemFolhaPagamentoUseCase,
      updateItemFolhaPagamentoUseCase,
      deleteItemFolhaPagamentoUseCase,
    },
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadSubContasDropDown,
  } = useDropDownLoader();

  const debouncedHandlerRef = useCreateDebounceHandler(750);

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const { setPageSaving, pushErrorNotification, pushInfoNotification } = usePageLoader();

  const { idUsuario } = useUserAuthStore();
  const [funcionarioInfo, setFuncionarioInfo] = useState<FuncionarioInformation>();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idFolhaPagamento, setIdFolhaPagamento] = useState<number>();

  const [selectedFolhaPagamento, setSelectedFolhaPagamento] = useState<FolhaPagamento>();
  const [selectedItemFolhaPagamento, setSelectedItemFolhaPagamento] = useState<ItemFolhaPagamento>();

  const [centroCustosDropdown, setCentroCustosDropdown] = useState<CentroCustoDropDown[]>();
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();
  const [funcionariosCadastroDropdown, setFuncionariosCadastroDropdown] = useState<FuncionarioDropDown[]>();
  const [subcontasDropdown, setSubcontasDropdown] = useState<SubcontaDropdown[]>();

  const [openCadastroItemFolhaPagamentoModal, setOpenCadastroItemFolhaPagamentoModal] = useState(false);
  const [openDeleteItemFolhaPagamentoModal, setOpenDeleteItemFolhaPagamentoModal] = useState(false);

  const [folhaPagamentoItens, setFolhaPagamentoItens] = useState<ItemFolhaPagamento[]>();

  const hookForm = useForm<ItemFolhaPagamentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ItemFolhaPagamentoSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: setErrorMessage,
  });

  const [idCentroCusto] = hookForm.watch(['idCentroCusto']);

  const filters = useFolhaPagamentoCadastroFilters();
  const pagination = useFolhaPagamentoCadastroPagination();

  const filteredFuncionariosDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      return x.canBeSelected;
    });
  }, [
    funcionariosDropdown,
  ]);

  const funcionariosResponsavelDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      if (funcionarioInfo) {
        if (x.idFuncionario === funcionarioInfo.idFuncionario) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    funcionarioInfo,
    funcionariosDropdown,
  ]);

  /**
   * Funções de carregamento
   */

  const loadFuncionarioInfo = useCallback(async () => {
    if (idUsuario) {
      try {
        const userInfo = await getFuncionarioByIdUsuarioUseCase
          .getFuncionarioByIdUsuario(idUsuario);
        setFuncionarioInfo(userInfo ?? undefined);
        return userInfo;
      } catch (e: unknown) {
        setFuncionarioInfo(undefined);
        throw new Error('Falha ao buscar dados do usuário!');
      }
    }
    return null;
  }, [
    idUsuario,
    getFuncionarioByIdUsuarioUseCase,
  ]);

  const loadFuncionariosCadastroDropdown = useCallback(async () => {
    try {
      if (idCentroCusto) {
        const funcionariosCadastro = await getFuncionariosByIdCentroCustoDropDownUseCase
          .getFuncionariosByIdCentroCustoDropDown(idCentroCusto, false);
        setFuncionariosCadastroDropdown(funcionariosCadastro);
      } else {
        setFuncionariosCadastroDropdown([]);
      }
    } catch (e: unknown) {
      setFuncionariosCadastroDropdown(undefined);
      throw e;
    }
  }, [
    idCentroCusto,
    getFuncionariosByIdCentroCustoDropDownUseCase,
  ]);

  const loadFolhaPagamento = useCallback(async (idFolhaPagamento: number) => {
    try {
      const folhaPagamento = await getFolhaPagamentoUseCase.getFolhaPagamento(idFolhaPagamento);
      if (folhaPagamento === null) throw new Error('Falha ao buscar folha de pagamento!');
      setSelectedFolhaPagamento(folhaPagamento);
    } catch (e: unknown) {
      setSelectedFolhaPagamento(undefined);
      throw e;
    }
  }, [
    getFolhaPagamentoUseCase,
  ]);

  const loadFolhaPagamentoItens = useCallback(async (funcState?: LoadFolhaPagamentoItensState) => {
    try {
      const filteredIdFolhaPagamento = funcState?.idFolhaPagamento ?? idFolhaPagamento ?? 0;
      const filteredIdCentroCusto = evaluateParamValue(funcState?.idCentroCusto, filters.idCentroCusto);
      const filteredIdFuncionario = evaluateParamValue(funcState?.idFuncionario, filters.idFuncionario);
      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllFolhaPagamentoItensUseCase.getAllFolhaPagamentoItens(
        filteredIdFolhaPagamento,
        filteredIdCentroCusto,
        filteredIdFuncionario,
        filteredPage,
        filteredRowsPerPage,
      );

      setFolhaPagamentoItens(getAllFolhaPagamentoItensUseCase.items);
      pagination.setTotalItems(getAllFolhaPagamentoItensUseCase.totalItems);
    } catch (e: unknown) {
      setFolhaPagamentoItens([]);
      throw e;
    }
  }, [
    idFolhaPagamento,
    filters,
    pagination,
    getAllFolhaPagamentoItensUseCase,
  ]);

  /**
   * UseEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id');
      const id = idFolhaPagamento ?? locationId ?? null;

      setErrorMessage(undefined);
      setIdFolhaPagamento(Number(id));

      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));
      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });
      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      await loadFuncionarioInfo();
      await loadFuncionariosCadastroDropdown();

      await loadFolhaPagamento(Number(id));

      useFolhaPagamentoCadastroPagination.setState({ page: 1 });
      await loadFolhaPagamentoItens({ idFolhaPagamento: Number(id), page: 1 });
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  useEffect(() => {
    (async function () {
      try {
        if (idCentroCusto === null || idCentroCusto === undefined) {
          hookForm.setValue('idFuncionarios', []);
        }
        await loadFuncionariosCadastroDropdown();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
      }
    })();
  }, [
    idCentroCusto,
    hookForm,
    pushErrorNotification,
    loadFuncionariosCadastroDropdown,
  ]);

  /**
   * Eventos
   * 
   */

  const handleSubmitItemFolhaPagamento = useCallback(async (form: ItemFolhaPagamentoForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (idFolhaPagamento) {
        await createItemFolhaPagamentoUseCase.createItemFolhaPagamento(
          idFolhaPagamento,
          form.idCentroCusto,
          form.idFuncionarios,
          form.idResponsavel,
          form.dataPagamento,
          form.idSubconta,
        );

        await loadFolhaPagamentoItens();
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } else {
        pushErrorNotification("Erro", ['Falha ao cadastro item de folha de pagamento: idFolhaPagamento invalido!']);
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao salvar item de folha de pagamento!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
      setOpenCadastroItemFolhaPagamentoModal(false);
    }
  }, [
    idFolhaPagamento,
    createItemFolhaPagamentoUseCase,
    pushErrorNotification,
    setPageSaving,
    loadFolhaPagamentoItens,
    pushInfoNotification,
  ]);

  const handleSubmitChanges = useCallback(async () => {
    try {
      setPageSaving(true);
      setSaving(true);
      if (selectedFolhaPagamento && folhaPagamentoItens && folhaPagamentoItens.length > 0) {
        for (let item of folhaPagamentoItens) {
          await updateItemFolhaPagamentoUseCase.updateItemFolhaPagamento(
            item.idItemFolhaPagamento,
            item.tituloConta,
            item.idSubconta,
            item.formaPagamento,
            item.valorPagar,
            item.observacaoFolha,
          );
        }
      }
      await loadFolhaPagamentoItens();
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao salvar item de folha de pagamento!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedFolhaPagamento,
    folhaPagamentoItens,
    updateItemFolhaPagamentoUseCase,
    pushErrorNotification,
    setPageSaving,
    loadFolhaPagamentoItens,
    pushInfoNotification,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadFuncionarioInfo();
      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));
      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });
      await loadSubContasDropDown(dt => setSubcontasDropdown(dt));

      await loadFolhaPagamento(idFolhaPagamento ?? 0);
      await loadFolhaPagamentoItens();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar informações da folha de pagamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    idFolhaPagamento,
    loadFuncionarioInfo,
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadSubContasDropDown,
    loadFolhaPagamento,
    loadFolhaPagamentoItens,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/FolhaPagamento_lista' });
  }, [
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useFolhaPagamentoCadastroPagination.setState({ page: page + 1 });
      await loadFolhaPagamentoItens({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar informações da folha de pagamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFolhaPagamentoItens,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useFolhaPagamentoCadastroPagination.setState({ page: 1, rowsPerPage });
      await loadFolhaPagamentoItens({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar informações da folha de pagamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFolhaPagamentoItens,
    pushErrorNotification,
  ]);

  const handleIdCentroCustoChange = useDebouncedCallback(debouncedHandlerRef, (idCentroCusto: number | null) => {
    useFolhaPagamentoCadastroFilters.setState({ idCentroCusto });
    return async () => {
      try {
        setLoading(true);
        useFolhaPagamentoCadastroPagination.setState({ page: 1 });
        await loadFolhaPagamentoItens({ idCentroCusto, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar informações da folha de pagamento!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadFolhaPagamentoItens,
    pushErrorNotification,
  ]);

  const handleIdFuncionarioChange = useDebouncedCallback(debouncedHandlerRef, (idFuncionario: number | null) => {
    useFolhaPagamentoCadastroFilters.setState({ idCentroCusto });
    filters.setIdFuncionario(idFuncionario);
    return async () => {
      try {
        setLoading(true);
        useFolhaPagamentoCadastroPagination.setState({ page: 1 });
        await loadFolhaPagamentoItens({ idFuncionario, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar informações da folha de pagamento!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadFolhaPagamentoItens,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useFolhaPagamentoCadastroFilters.getState().clearFilters();
      useFolhaPagamentoCadastroPagination.setState({ page: 1 });
      await loadFolhaPagamentoItens({ idFuncionario: null, idCentroCusto: null, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar informações da folha de pagamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFolhaPagamentoItens,
    pushErrorNotification,
  ]);

  const handleItemFolhaPagamentoTituloChange = useCallback((idItemFolhaPagamento: number, valor: string) => {
    setFolhaPagamentoItens(items => {
      if (items === undefined) return items;
      const itemIndex = items.findIndex(x => x.idItemFolhaPagamento === idItemFolhaPagamento);
      if (itemIndex >= 0) {
        items[itemIndex].tituloConta = valor;
      }
      return [...items];
    });
  }, []);

  const handleItemFolhaPagamentoSubcontaChange = useCallback((idItemFolhaPagamento: number, idSubconta: number | null) => {
    setFolhaPagamentoItens(items => {
      if (items === undefined) return items;
      const itemIndex = items.findIndex(x => x.idItemFolhaPagamento === idItemFolhaPagamento);
      if (itemIndex >= 0) {
        items[itemIndex].idSubconta = idSubconta;
      }
      return [...items];
    });
  }, []);

  const handleItemFolhaPagamentoFormaPagamentoChange = useCallback((idItemFolhaPagamento: number, valor: string) => {
    setFolhaPagamentoItens(items => {
      if (items === undefined) return items;
      const itemIndex = items.findIndex(x => x.idItemFolhaPagamento === idItemFolhaPagamento);
      if (itemIndex >= 0) {
        items[itemIndex].formaPagamento = valor;
      }
      return [...items];
    });
  }, []);

  const handleItemFolhaPagamentoValorChange = useCallback((idItemFolhaPagamento: number, valor: number | null) => {
    setFolhaPagamentoItens(items => {
      if (items === undefined) return items;
      const itemIndex = items.findIndex(x => x.idItemFolhaPagamento === idItemFolhaPagamento);
      if (itemIndex >= 0) {
        items[itemIndex].valorPagar = valor;
      }
      return [...items];
    });
  }, []);

  const handleItemFolhaPagamentoInfoGeraisChange = useCallback((idItemFolhaPagamento: number, valor: string) => {
    setFolhaPagamentoItens(items => {
      if (items === undefined) return items;
      const itemIndex = items.findIndex(x => x.idItemFolhaPagamento === idItemFolhaPagamento);
      if (itemIndex >= 0) {
        items[itemIndex].observacaoFolha = valor;
      }
      return [...items];
    });
  }, []);

  const handleItemFolhaPagamentoDetails = useCallback((idItemFolhaPagamento: number, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/FolhaPagamento_itensverba?id=${idItemFolhaPagamento}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/FolhaPagamento_itensverba?id=${idItemFolhaPagamento}`);
    }
  }, [
    backOrNavigate,
  ])

  const handleOpenCadastroItemFolhaPagamentoModal = useCallback((idItemFolhaPagamento: number | null, openState: boolean) => {
    setOpenCadastroItemFolhaPagamentoModal(openState);
  }, [
  ]);

  const handleDeleteFolhaPagamentoOpenState = useCallback((idItemFolhaPagamento: number | null, openState: boolean) => {
    if (openState) {
      if (idItemFolhaPagamento && folhaPagamentoItens) {
        setSelectedItemFolhaPagamento(folhaPagamentoItens.find(x => x.idItemFolhaPagamento === idItemFolhaPagamento));
      } else {
        setSelectedItemFolhaPagamento(undefined);
      }
    }
    setOpenDeleteItemFolhaPagamentoModal(openState);
  }, [
    folhaPagamentoItens,
  ]);

  const handleConfirmarDeletarItemFolhaPagamento = useCallback(async () => {
    try {
      if (selectedItemFolhaPagamento) {
        setSaving(true);

        await deleteItemFolhaPagamentoUseCase.deleteItemFolhaPagamento(
          selectedItemFolhaPagamento.idItemFolhaPagamento,
        );

        await loadFolhaPagamentoItens();
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao deletar item da folha de pagamento!'));
    } finally {
      setSaving(false);
      setOpenDeleteItemFolhaPagamentoModal(false);
    }
  }, [
    selectedItemFolhaPagamento,
    deleteItemFolhaPagamentoUseCase,
    pushErrorNotification,
    loadFolhaPagamentoItens,
    pushInfoNotification,
  ]);

  const handleOpenChangeHistory = useCallback((
    idTabela: number | null,
    tipoTabela: LogTipoTabela | null,
  ) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idFolhaPagamento) {
        changeHistory.handleOpenChangeHistory(idFolhaPagamento, LogTipoTabela.FOLHA_PAGAMENTO, true);
      }
    }
  }, [
    idFolhaPagamento,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    funcionarioInfo: funcionarioInfo ?? null,
    idFolhaPagamento,
    selectedFolhaPagamento,
    selectedItemFolhaPagamento,
    centroCustosDropdown: centroCustosDropdown ?? [],
    funcionariosDropdown: filteredFuncionariosDropdown ?? [],
    funcionariosResponsavelDropdown: funcionariosResponsavelDropdown ?? [],
    funcionariosCadastroDropdown: funcionariosCadastroDropdown ?? [],
    subcontasDropdown: subcontasDropdown ?? [],
    openCadastroItemFolhaPagamentoModal,
    openDeleteItemFolhaPagamentoModal,
    folhaPagamentoItens: folhaPagamentoItens ?? [],
    hookForm,
    filters,
    pagination,
    changeHistory,
    handleSubmitItemFolhaPagamento,
    handleSubmitChanges,
    handleReload,
    handleBack,
    handlePageChange,
    handleRowsPerPageChange,
    handleIdCentroCustoChange,
    handleIdFuncionarioChange,
    handleClearFilters,
    handleItemFolhaPagamentoTituloChange,
    handleItemFolhaPagamentoSubcontaChange,
    handleItemFolhaPagamentoFormaPagamentoChange,
    handleItemFolhaPagamentoValorChange,
    handleItemFolhaPagamentoInfoGeraisChange,
    handleItemFolhaPagamentoDetails,
    handleOpenCadastroItemFolhaPagamentoModal,
    handleDeleteFolhaPagamentoOpenState,
    handleConfirmarDeletarItemFolhaPagamento,
    handleOpenChangeHistory,
  };
}